import {
  Contact,
  CubeReducerState,
  MerchantBranding,
  Phase,
  Price,
  QuoteContact,
  CounterSigner,
  QuoteDealType
} from 'modules/Cube/domain/cube.domain.types'
import { v1ApiQuote } from 'modules/Cube/communication/external/quotes.api.v1/ports/entityLoaders/useLoadQuote'
import { quoteStatusAdapter } from 'modules/Cube/utils/status.adapter'
import { INITIAL_CUBE_STATE } from 'modules/Cube/domain/cube.constants'
import { Currency } from '@sequencehq/api'
import {
  apiDatesAdapters,
  differenceInDays,
  parse,
  type Duration
} from '@sequencehq/utils/dates'
import { v1ApiIntegration } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders/useLoadIntegrations'
import { v1ApiPrice } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders/useLoadPrices'
import { v1ApiProduct } from 'modules/Cube/communication/external/common.api.v1/ports/entityLoaders/useLoadProducts'
import { arrayToIdKeyedObject } from '@sequencehq/utils'
import { apiPresentationToQuotePresentation } from '@sequencehq/quote-content'
import { v1ApiUser } from 'modules/Cube/communication/external/quotes.api.v1/ports/entityLoaders/useLoadUser'
import { ApiCustomer } from '../../common.api.v1/ports/entityLoaders/useLoadSingleCustomer'
import {
  calculateContractLength,
  pricesToDomainPrices,
  quotePhasesToDiscounts,
  quotePhasesToMinimums,
  quotePhasesToPhases
} from '../../common.api.v1/adapters/common.adapters.in'

type QuoteSettings = {
  counterSigners?: { name: string; email: string }[]
  isAutoExecuteQuotesEnabled: boolean
  customerPortalAuthRequired?: 'REQUIRED' | 'NOT_REQUIRED'
}

export type ApiData = {
  quote: v1ApiQuote
  presentation: any
  customers: ApiCustomer[]
  integrations: v1ApiIntegration[]
  prices: v1ApiPrice[]
  products: v1ApiProduct[]
  currencies: Currency[]
  merchantBranding: MerchantBranding
  createdByUser: v1ApiUser | undefined
  quoteSettings: QuoteSettings
  activeQuoteApprovalAutomationVersionId: string | undefined
  runningQuoteApprovalAutomationExecutionId: string | undefined
  runningQuoteApprovalAutomationExecutionCreateTime: string | undefined
}

export const quoteResponseToCommonFields = (
  quote: v1ApiQuote,
  enabledCurrencies: Currency[],
  phases: Phase[]
): CubeReducerState['data']['common'] => ({
  id: quote.id,
  title: quote.title ?? '',
  alias: quote.quoteNumber,
  customerId: quote.customerId ?? '',
  status: quoteStatusAdapter.in(quote.status),
  currency: quote.currency ?? enabledCurrencies[0],
  startDate: quote.preliminaryBillingStartDate
    ? apiDatesAdapters.fromApi(quote.preliminaryBillingStartDate)
    : undefined,
  isArchived: !!quote.archivedAt,
  phaseIds: phases.map(phase => phase.id),
  createdAt: new Date(quote.createdAt)
})

export const calculateExpiryDays = (
  quote: v1ApiQuote
): Duration | undefined => {
  if ('expiryDays' in quote) {
    return { days: quote.expiryDays }
  }

  if (quote.expiresAt) {
    const quoteExpiresAt = new Date(quote.expiresAt)
    const now = new Date()

    return { days: differenceInDays(quoteExpiresAt, now) }
  }

  return {
    days: 30
  }
}

export const amendmentOriginalToDomain = (
  original: NonNullable<v1ApiQuote['amendment']>['original']
): NonNullable<CubeReducerState['data']['quote']['amendment']>['original'] => ({
  phases: original.phases.map(
    ({ startDate, endDate, prices, discounts, minimums }) => ({
      startDate,
      endDate,
      prices,
      discounts,
      minimums
    })
  )
})

export const quoteResponseToFields = ({
  quote,
  user,
  quoteSettings,
  activeQuoteApprovalAutomationVersionId,
  runningQuoteApprovalAutomationExecutionId,
  runningQuoteApprovalAutomationExecutionCreateTime
}: {
  quote: v1ApiQuote
  user: v1ApiUser | undefined
  quoteSettings: QuoteSettings | undefined
  activeQuoteApprovalAutomationVersionId: string | undefined
  runningQuoteApprovalAutomationExecutionId: string | undefined
  runningQuoteApprovalAutomationExecutionCreateTime: string | undefined
}): CubeReducerState['data']['quote'] => {
  const contacts = quote.contacts ?? []
  const contactIds = new Set(contacts.map(contact => contact.contactId))
  // Recipients modelled as contacts remain contacts, so duplicates are removed from CC.
  const ccContacts = (quote.ccContacts ?? []).filter(
    contact => !contactIds.has(contact.contactId)
  )

  return {
    quoteNumber: quote.quoteNumber,
    contractLength: calculateContractLength(
      quote.phases?.map(({ duration }) => duration) ?? []
    ),
    dealType: (quote.dealType ?? 'NEW_BUSINESS') as QuoteDealType,
    expiresIn: calculateExpiryDays(quote),
    publishedAt: quote.publishedAt ? new Date(quote.publishedAt) : undefined,
    acceptedAt: quote.acceptedAt ? new Date(quote.acceptedAt) : undefined,
    attachmentAssets: quote.attachmentAssets,
    readyToSignAt:
      'readyToSignAt' in quote && quote.readyToSignAt
        ? new Date(quote.readyToSignAt)
        : undefined,
    executedAt:
      'executedAt' in quote && quote.executedAt
        ? new Date(quote.executedAt)
        : undefined,
    expiresAt:
      'expiresAt' in quote && quote.expiresAt
        ? new Date(quote.expiresAt)
        : undefined,
    billingScheduleId: quote.billingScheduleId,
    amendment: quote.amendment
      ? {
          billingScheduleId: quote.amendment.billingScheduleId,
          effectiveFrom: parse(
            quote.amendment.effectiveFrom,
            'yyyy-MM-dd',
            new Date()
          ),
          supersededByQuoteId: quote.amendment.supersededByQuoteId,
          outForSignatureQuoteId: quote.amendment.outForSignatureQuoteId,
          original: amendmentOriginalToDomain(quote.amendment.original)
        }
      : undefined,
    createdByEmail: user?.email,
    contacts,
    ccContacts,
    isSignaturesEnabled: quote.isSignaturesEnabled,
    counterSigners: quote.counterSigners,
    isInternalSignerEnabled: Boolean(quoteSettings?.counterSigners?.length),
    customerLegalName: quote.customerLegalName,
    customerAddress: quote.customerAddress,
    salesforceOpportunityId: quote.salesforceOpportunityId,
    integrationIds: quote.integrationIds ?? [],
    pdfStatus: quote.pdfStatus,
    isExpired: quote.isExpired,
    defaultDueDateDays: quote.defaultDueDateDays,
    isAutoExecuteQuotesEnabled:
      quoteSettings?.isAutoExecuteQuotesEnabled ?? false,
    isCustomerPortalAuthRequired:
      quoteSettings?.customerPortalAuthRequired === 'REQUIRED',
    signatureMode: quote.signatureMode,
    activeQuoteApprovalAutomationVersionId,
    runningQuoteApprovalAutomationExecutionId,
    runningQuoteApprovalAutomationExecutionCreateTime
  }
}

export const apiResponseToFields = (
  apiData: ApiData
): Pick<CubeReducerState, 'data'> => {
  const transformedPhases = quotePhasesToPhases(apiData.quote.phases)
  const currency = apiData.quote.currency ?? apiData.currencies[0]

  /**
   * Keep the presentation object as it is when loading an ACCEPTED or EXECUTED quote to prevent 400 errors
   * when, for example, attempting to change the start date for a quote
   */
  const skipPresentationUpgrade = ['ACCEPTED', 'EXECUTED'].includes(
    apiData.quote.status
  )

  return {
    data: {
      common: quoteResponseToCommonFields(
        apiData.quote,
        apiData.currencies,
        Object.values(transformedPhases)
      ),
      quote: quoteResponseToFields({
        quote: apiData.quote,
        user: apiData.createdByUser,
        quoteSettings: apiData.quoteSettings,
        activeQuoteApprovalAutomationVersionId:
          apiData.activeQuoteApprovalAutomationVersionId,
        runningQuoteApprovalAutomationExecutionId:
          apiData.runningQuoteApprovalAutomationExecutionId,
        runningQuoteApprovalAutomationExecutionCreateTime:
          apiData.runningQuoteApprovalAutomationExecutionCreateTime
      }),
      presentation: apiPresentationToQuotePresentation({
        presentation: apiData.presentation,
        skipPresentationUpgrade
      }),
      phases: transformedPhases,
      schedule: INITIAL_CUBE_STATE.data.schedule,
      customers: arrayToIdKeyedObject(apiData.customers),
      taxRates: {},
      products: arrayToIdKeyedObject(apiData.products),
      discounts: quotePhasesToDiscounts(apiData.quote.phases),
      minimums: quotePhasesToMinimums(apiData.quote.phases),
      // Quotes do not carry credit grants.
      creditGrants: {},
      prices: arrayToIdKeyedObject<Price>(
        pricesToDomainPrices(apiData.prices, currency)
      ),
      merchantBranding: apiData.merchantBranding
    }
  }
}

export const contactToQuoteContact: (contact: Contact) => QuoteContact =
  contact => {
    return {
      contactId: contact.id,
      name: contact.name,
      email: contact.email,
      signedAt: undefined
    }
  }

export const counterSignerToQuoteCounterSigner = (signer: CounterSigner) => ({
  id: signer.id,
  name: signer.name,
  email: signer.email,
  signedAt: signer.signedAt
})

export const counterSignersToOptions = (counterSigners: CounterSigner[]) => {
  return counterSigners.map(signer => ({
    value: signer.email,
    label: signer.name || signer.email,
    secondaryLabel: signer.name ? signer.email : undefined
  }))
}
